import React, { useEffect, useState, useRef, createElement } from 'react';
import { Canvas, Rect, Text, Textbox, Line, FabricObject, FabricImage } from 'fabric';
import { Product, Ingredient, Language, SavedLabel } from '../types';
import { translateLabel, translateAllergen, isRTL } from '../utils/translations';
import { translateIngredient, translateProduct } from '../utils/ingredientTranslations';
import { X, Download, Plus, Type, Hash, Trash2, Image as ImageIcon, Save, Bold, Italic } from 'lucide-react';
interface LabelDesignerProps {
  product: Product;
  ingredients: Ingredient[];
  language: Language;
  existingLabel?: SavedLabel;
  onClose: () => void;
  onSave: (label: Omit<SavedLabel, 'id' | 'createdAt' | 'updatedAt'>) => void;
}
interface LabelVariable {
  id: string;
  label: string;
  value: string;
  icon: string;
}
export function LabelDesigner({
  product,
  ingredients,
  language,
  existingLabel,
  onClose,
  onSave
}: LabelDesignerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<Canvas | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [freeText, setFreeText] = useState('');
  const [textFontSize, setTextFontSize] = useState(14);
  const [textBold, setTextBold] = useState(false);
  const [textItalic, setTextItalic] = useState(false);
  const [selectedObject, setSelectedObject] = useState<FabricObject | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Predefined variables
  const variables: LabelVariable[] = [{
    id: 'weight',
    label: language === 'de' ? 'Gewicht' : language === 'en' ? 'Weight' : language === 'fr' ? 'Poids' : language === 'es' ? 'Peso' : language === 'it' ? 'Peso' : language === 'ar' ? 'الوزن' : 'Weight',
    value: '250g',
    icon: '⚖️'
  }, {
    id: 'batch',
    label: language === 'de' ? 'Chargennummer' : language === 'en' ? 'Batch Number' : language === 'fr' ? 'Numéro de lot' : language === 'es' ? 'Número de lote' : language === 'it' ? 'Numero di lotto' : language === 'ar' ? 'رقم الدفعة' : 'Batch',
    value: 'L2025001',
    icon: '#️⃣'
  }, {
    id: 'production',
    label: language === 'de' ? 'Hergestellt am' : language === 'en' ? 'Produced on' : language === 'fr' ? 'Produit le' : language === 'es' ? 'Producido el' : language === 'it' ? 'Prodotto il' : language === 'ar' ? 'تاريخ الإنتاج' : 'Produced',
    value: '15.01.2025',
    icon: '📅'
  }, {
    id: 'price',
    label: language === 'de' ? 'Preis' : language === 'en' ? 'Price' : language === 'fr' ? 'Prix' : language === 'es' ? 'Precio' : language === 'it' ? 'Prezzo' : language === 'ar' ? 'السعر' : 'Price',
    value: '€ 4,99',
    icon: '💰'
  }, {
    id: 'storage',
    label: language === 'de' ? 'Lagerung' : language === 'en' ? 'Storage' : language === 'fr' ? 'Conservation' : language === 'es' ? 'Almacenamiento' : language === 'it' ? 'Conservazione' : language === 'ar' ? 'التخزين' : 'Storage',
    value: language === 'de' ? 'Kühl lagern' : language === 'en' ? 'Keep refrigerated' : language === 'fr' ? 'Conserver au frais' : language === 'es' ? 'Conservar en frío' : language === 'it' ? 'Conservare in frigorifero' : language === 'ar' ? 'يحفظ بارداً' : 'Keep cool',
    icon: '❄️'
  }];
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Bitte wählen Sie eine Bilddatei aus.');
      return;
    }
    const reader = new FileReader();
    reader.onload = event => {
      const imageUrl = event.target?.result as string;
      setUploadedImage(imageUrl);
    };
    reader.readAsDataURL(file);
  };
  const addImageToCanvas = () => {
    if (!fabricCanvasRef.current || !uploadedImage) return;
    FabricImage.fromURL(uploadedImage).then(img => {
      const maxSize = 150;
      const scale = Math.min(maxSize / (img.width || 1), maxSize / (img.height || 1));
      img.set({
        left: 200,
        top: 50,
        scaleX: scale,
        scaleY: scale,
        originX: 'center',
        originY: 'center'
      });
      fabricCanvasRef.current!.add(img);
      fabricCanvasRef.current!.setActiveObject(img);
      fabricCanvasRef.current!.renderAll();
      setUploadedImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    });
  };
  const addFreeTextToCanvas = () => {
    if (!fabricCanvasRef.current || !freeText.trim()) return;
    const text = new Textbox(freeText, {
      left: 200,
      top: 300,
      width: 300,
      fontSize: textFontSize,
      fill: '#000000',
      fontFamily: 'Arial',
      fontWeight: textBold ? 'bold' : 'normal',
      fontStyle: textItalic ? 'italic' : 'normal',
      textAlign: 'left'
    });
    fabricCanvasRef.current.add(text);
    fabricCanvasRef.current.setActiveObject(text);
    fabricCanvasRef.current.renderAll();
    setFreeText('');
  };
  const addVariableToCanvas = (variable: LabelVariable) => {
    if (!fabricCanvasRef.current) return;
    const text = new Textbox(`${variable.label}: ${variable.value}`, {
      left: 200,
      top: 300,
      width: 300,
      fontSize: 12,
      fill: '#000000',
      fontFamily: 'Arial',
      textAlign: 'left'
    });
    fabricCanvasRef.current.add(text);
    fabricCanvasRef.current.setActiveObject(text);
    fabricCanvasRef.current.renderAll();
  };
  const deleteSelectedObject = () => {
    if (!fabricCanvasRef.current || !selectedObject) return;
    fabricCanvasRef.current.remove(selectedObject);
    fabricCanvasRef.current.renderAll();
    setSelectedObject(null);
  };
  const createDefaultLabel = (canvas: Canvas, lang: Language) => {
    const rtl = isRTL(lang);
    const productIngredients = product.ingredientIds.map(id => ingredients.find(i => i.id === id)).filter(Boolean) as Ingredient[];
    const allergenIds = new Set<string>();
    productIngredients.forEach(ing => {
      ing.allergens.forEach(a => allergenIds.add(a));
    });
    const border = new Rect({
      left: 10,
      top: 10,
      width: 380,
      height: 580,
      fill: 'transparent',
      stroke: '#000000',
      strokeWidth: 2,
      rx: 5,
      ry: 5,
      selectable: false
    });
    const translatedProductName = translateProduct(product.name, lang);
    const productName = new Textbox(translatedProductName.toUpperCase(), {
      left: 200,
      top: 40,
      width: 340,
      fontSize: 32,
      fill: '#000000',
      fontFamily: 'Arial',
      fontWeight: 'bold',
      textAlign: 'center',
      originX: 'center'
    });
    const ingredientsLabelText = translateLabel('ingredients', lang) + ':';
    const ingredientsLabel = new Text(ingredientsLabelText, {
      left: rtl ? 360 : 40,
      top: 120,
      fontSize: 14,
      fill: '#000000',
      fontFamily: 'Arial',
      fontWeight: 'bold',
      originX: rtl ? 'right' : 'left'
    });
    let ingredientsText = '';
    const boldRanges: {
      start: number;
      end: number;
    }[] = [];
    productIngredients.forEach((ing, index) => {
      const startPos = ingredientsText.length;
      if (index > 0) {
        ingredientsText += ', ';
      }
      const translatedName = translateIngredient(ing.name, lang);
      ingredientsText += translatedName;
      if (ing.allergens.length > 0) {
        const ingredientStart = startPos + (index > 0 ? 2 : 0);
        const ingredientEnd = ingredientsText.length;
        boldRanges.push({
          start: ingredientStart,
          end: ingredientEnd
        });
      }
    });
    const ingredientsTextbox = new Textbox(ingredientsText, {
      left: rtl ? 360 : 40,
      top: 145,
      width: 320,
      fontSize: 13,
      fill: '#000000',
      fontFamily: 'Arial',
      textAlign: rtl ? 'right' : 'left',
      originX: rtl ? 'right' : 'left'
    });
    boldRanges.forEach(range => {
      for (let i = range.start; i < range.end; i++) {
        ingredientsTextbox.setSelectionStyles({
          fontWeight: 'bold'
        }, i, i + 1);
      }
    });
    let currentY = 240;
    if (allergenIds.size > 0) {
      const allergensList = Array.from(allergenIds).map(id => translateAllergen(id, lang)).join(', ');
      const allergensWarning = new Textbox(translateLabel('contains', lang) + ': ' + allergensList, {
        left: rtl ? 360 : 40,
        top: currentY,
        width: 320,
        fontSize: 11,
        fill: '#000000',
        fontFamily: 'Arial',
        fontWeight: 'bold',
        textAlign: rtl ? 'right' : 'left',
        originX: rtl ? 'right' : 'left'
      });
      canvas.add(allergensWarning);
      currentY += 60;
    }
    const bestBeforeLabel = new Text(lang === 'de' ? 'Mindestens haltbar bis:' : lang === 'en' ? 'Best before:' : lang === 'fr' ? 'À consommer avant le:' : lang === 'es' ? 'Consumir preferentemente antes del:' : lang === 'it' ? 'Da consumarsi preferibilmente entro:' : lang === 'ar' ? 'يفضل استهلاكه قبل:' : 'Best before:', {
      left: 200,
      top: currentY + 20,
      fontSize: 12,
      fill: '#000000',
      fontFamily: 'Arial',
      textAlign: 'center',
      originX: 'center'
    });
    const bestBeforeDate = new Text('31.12.2025', {
      left: 200,
      top: currentY + 45,
      fontSize: 18,
      fill: '#000000',
      fontFamily: 'Arial',
      fontWeight: 'bold',
      textAlign: 'center',
      originX: 'center'
    });
    const barcodeY = currentY + 90;
    const barcodeLines: Line[] = [];
    const barcodeWidth = 200;
    const barcodeHeight = 60;
    const barcodeX = 100;
    const lineCount = 40;
    for (let i = 0; i < lineCount; i++) {
      const lineWidth = Math.random() > 0.5 ? 2 : 1;
      const line = new Line([barcodeX + i * (barcodeWidth / lineCount), barcodeY, barcodeX + i * (barcodeWidth / lineCount), barcodeY + barcodeHeight], {
        stroke: '#000000',
        strokeWidth: lineWidth,
        selectable: false
      });
      barcodeLines.push(line);
    }
    const barcodeNumber = new Text('4123456789018', {
      left: 200,
      top: barcodeY + barcodeHeight + 5,
      fontSize: 12,
      fill: '#000000',
      fontFamily: 'Courier New',
      textAlign: 'center',
      originX: 'center',
      selectable: false
    });
    const companyName = new Text('Ihre Firma GmbH', {
      left: 200,
      top: 520,
      fontSize: 10,
      fill: '#000000',
      fontFamily: 'Arial',
      textAlign: 'center',
      originX: 'center'
    });
    const companyAddress = new Text('Musterstraße 123', {
      left: 200,
      top: 540,
      fontSize: 9,
      fill: '#000000',
      fontFamily: 'Arial',
      textAlign: 'center',
      originX: 'center'
    });
    const companyCity = new Text('D-12345 Musterstadt', {
      left: 200,
      top: 555,
      fontSize: 9,
      fill: '#000000',
      fontFamily: 'Arial',
      textAlign: 'center',
      originX: 'center'
    });
    canvas.add(border);
    canvas.add(productName);
    canvas.add(ingredientsLabel);
    canvas.add(ingredientsTextbox);
    canvas.add(bestBeforeLabel);
    canvas.add(bestBeforeDate);
    barcodeLines.forEach(line => canvas.add(line));
    canvas.add(barcodeNumber);
    canvas.add(companyName);
    canvas.add(companyAddress);
    canvas.add(companyCity);
  };
  const handleSaveLabel = async () => {
    if (!fabricCanvasRef.current) {
      console.error('Canvas not initialized');
      alert('Fehler: Canvas nicht initialisiert');
      return;
    }
    setIsSaving(true);
    try {
      console.log('Starting label save...');
      const thumbnail = fabricCanvasRef.current.toDataURL({
        format: 'png',
        quality: 0.8,
        multiplier: 0.5
      });
      console.log('Thumbnail generated, size:', thumbnail.length);
      const canvasJSON = fabricCanvasRef.current.toJSON(['selectable', 'evented']);
      const canvasData = JSON.stringify(canvasJSON);
      console.log('Canvas JSON generated, size:', canvasData.length);
      onSave({
        language,
        canvasData,
        thumbnail
      });
      console.log('Label saved successfully');
      setTimeout(() => {
        onClose();
      }, 300);
    } catch (error) {
      console.error('Error saving label:', error);
      alert(`Fehler beim Speichern des Etiketts: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    } finally {
      setIsSaving(false);
    }
  };
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = new Canvas(canvasRef.current, {
      width: 400,
      height: 600,
      backgroundColor: '#ffffff'
    });
    fabricCanvasRef.current = canvas;
    if (existingLabel) {
      console.log('Loading existing label for language:', existingLabel.language);
      try {
        const canvasData = JSON.parse(existingLabel.canvasData);
        canvas.loadFromJSON(canvasData).then(() => {
          console.log('Existing label loaded successfully');
          canvas.renderAll();
          setIsReady(true);
        }).catch(error => {
          console.error('Error loading existing label:', error);
          alert('Fehler beim Laden des gespeicherten Etiketts');
        });
        return () => {
          canvas.dispose();
        };
      } catch (error) {
        console.error('Error parsing existing label:', error);
      }
    }
    canvas.on('selection:created', e => {
      setSelectedObject(e.selected?.[0] || null);
    });
    canvas.on('selection:updated', e => {
      setSelectedObject(e.selected?.[0] || null);
    });
    canvas.on('selection:cleared', () => {
      setSelectedObject(null);
    });
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && canvas.getActiveObject()) {
        const activeObject = canvas.getActiveObject();
        if (activeObject) {
          canvas.remove(activeObject);
          canvas.renderAll();
          setSelectedObject(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    if (!existingLabel) {
      console.log('Creating new label for language:', language);
      createDefaultLabel(canvas, language);
      setIsReady(true);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      canvas.dispose();
    };
  }, [product, ingredients, language, existingLabel]);
  const handleDownload = () => {
    if (!fabricCanvasRef.current) return;
    const dataURL = fabricCanvasRef.current.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: 3
    });
    const link = document.createElement('a');
    link.download = `${product.name}-label-${language}.png`;
    link.href = dataURL;
    link.click();
  };
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-id="element-178">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col" data-id="element-179">
        <div className="flex items-center justify-between p-6 border-b bg-gray-50" data-id="element-180">
          <div data-id="element-181">
            <h2 className="text-xl font-semibold" data-id="element-182">Etikett Designer</h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-183">
              {product.name} • Sprache: {language.toUpperCase()}
              {existingLabel && <span className="ml-2 text-green-600" data-id="element-184">• Bearbeiten</span>}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-185">
            <button onClick={handleSaveLabel} disabled={isSaving} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400" data-id="element-186">
              <Save className="w-4 h-4" data-id="element-187" />
              {isSaving ? 'Speichert...' : 'Etikett speichern'}
            </button>
            <button onClick={handleDownload} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-188">
              <Download className="w-4 h-4" data-id="element-189" />
              Download
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-190">
              <X className="w-5 h-5" data-id="element-191" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex" data-id="element-192">
          <div className="flex-1 overflow-auto p-8 bg-gray-100" data-id="element-193">
            <div className="flex justify-center" data-id="element-194">
              <div className="bg-white rounded-lg shadow-2xl p-6" data-id="element-195">
                <canvas ref={canvasRef} data-id="element-196" />
              </div>
            </div>
          </div>

          <div className="w-80 border-l bg-white overflow-y-auto" data-id="element-197">
            <div className="p-6 space-y-6" data-id="element-198">
              {selectedObject && <div className="pb-6 border-b border-gray-200" data-id="element-199">
                  <button onClick={deleteSelectedObject} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium" data-id="element-200">
                    <Trash2 className="w-4 h-4" data-id="element-201" />
                    Element löschen
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-202">
                    oder drücken Sie{' '}
                    <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono" data-id="element-203">
                      Entf
                    </kbd>
                  </p>
                </div>}

              <div data-id="element-204">
                <div className="flex items-center gap-2 mb-3" data-id="element-205">
                  <ImageIcon className="w-5 h-5 text-gray-700" data-id="element-206" />
                  <h3 className="font-semibold text-gray-900" data-id="element-207">
                    Bild hochladen
                  </h3>
                </div>
                <div className="space-y-3" data-id="element-208">
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="image-upload" data-id="element-209" />
                  <label htmlFor="image-upload" className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer group" data-id="element-210">
                    <ImageIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-600" data-id="element-211" />
                    <span className="text-sm text-gray-600 group-hover:text-blue-600 font-medium" data-id="element-212">
                      Bild auswählen
                    </span>
                  </label>

                  {uploadedImage && <div className="space-y-2" data-id="element-213">
                      <div className="relative w-full h-32 bg-gray-100 rounded-lg overflow-hidden border border-gray-200" data-id="element-214">
                        <img src={uploadedImage} alt="Vorschau" className="w-full h-full object-contain" data-id="element-215" />
                      </div>
                      <button onClick={addImageToCanvas} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium" data-id="element-216">
                        <Plus className="w-4 h-4" data-id="element-217" />
                        Bild zum Etikett hinzufügen
                      </button>
                    </div>}
                  <p className="text-xs text-gray-500" data-id="element-218">
                    Logo, Produktfoto oder andere Bilder (PNG, JPG, SVG)
                  </p>
                </div>
              </div>

              <div data-id="element-219">
                <div className="flex items-center gap-2 mb-3" data-id="element-220">
                  <Type className="w-5 h-5 text-gray-700" data-id="element-221" />
                  <h3 className="font-semibold text-gray-900" data-id="element-222">Freitext</h3>
                </div>
                <div className="space-y-3" data-id="element-223">
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200" data-id="element-224">
                    <button onClick={() => setTextBold(!textBold)} className={`p-2 rounded transition-colors ${textBold ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'}`} title="Fett" data-id="element-225">
                      <Bold className="w-4 h-4" data-id="element-226" />
                    </button>
                    <button onClick={() => setTextItalic(!textItalic)} className={`p-2 rounded transition-colors ${textItalic ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'}`} title="Kursiv" data-id="element-227">
                      <Italic className="w-4 h-4" data-id="element-228" />
                    </button>
                    <div className="h-6 w-px bg-gray-300 mx-1" data-id="element-229"></div>
                    <select value={textFontSize} onChange={e => setTextFontSize(Number(e.target.value))} className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-230">
                      <option value="8" data-id="element-231">8px</option>
                      <option value="10" data-id="element-232">10px</option>
                      <option value="12" data-id="element-233">12px</option>
                      <option value="14" data-id="element-234">14px</option>
                      <option value="16" data-id="element-235">16px</option>
                      <option value="18" data-id="element-236">18px</option>
                      <option value="20" data-id="element-237">20px</option>
                      <option value="24" data-id="element-238">24px</option>
                      <option value="28" data-id="element-239">28px</option>
                      <option value="32" data-id="element-240">32px</option>
                      <option value="36" data-id="element-241">36px</option>
                      <option value="48" data-id="element-242">48px</option>
                    </select>
                  </div>

                  <textarea value={freeText} onChange={e => setFreeText(e.target.value)} placeholder="Eigenen Text eingeben..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none" rows={3} style={{
                  fontWeight: textBold ? 'bold' : 'normal',
                  fontStyle: textItalic ? 'italic' : 'normal',
                  fontSize: `${textFontSize}px`
                }} data-id="element-243" />
                  <button onClick={addFreeTextToCanvas} disabled={!freeText.trim()} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-244">
                    <Plus className="w-4 h-4" data-id="element-245" />
                    Text hinzufügen
                  </button>
                </div>
              </div>

              <div data-id="element-246">
                <div className="flex items-center gap-2 mb-3" data-id="element-247">
                  <Hash className="w-5 h-5 text-gray-700" data-id="element-248" />
                  <h3 className="font-semibold text-gray-900" data-id="element-249">Variablen</h3>
                </div>
                <div className="space-y-2" data-id="element-250">
                  {variables.map(variable => <button key={variable.id} onClick={() => addVariableToCanvas(variable)} className="w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group" data-id="element-251">
                      <span className="text-2xl" data-id="element-252">{variable.icon}</span>
                      <div className="flex-1 min-w-0" data-id="element-253">
                        <p className="font-medium text-gray-900 text-sm group-hover:text-blue-600" data-id="element-254">
                          {variable.label}
                        </p>
                        <p className="text-xs text-gray-500 truncate" data-id="element-255">
                          {variable.value}
                        </p>
                      </div>
                      <Plus className="w-4 h-4 text-gray-400 group-hover:text-blue-600" data-id="element-256" />
                    </button>)}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-257">
                <p className="text-xs text-blue-800" data-id="element-258">
                  💡 <strong data-id="element-259">Tipp:</strong> Neue Etiketten werden automatisch
                  mit den aktuellen Produktdaten in der gewählten Sprache
                  erstellt.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}